import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import TodayPanel from "../../components/admin/TodayPanel";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [instances, setInstances] = useState([]);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/bookings").then(r => setBookings(r.data.slice(0, 5)));
    api.get("/instances?upcoming_only=true").then(r => setInstances(r.data.slice(0, 5)));
    api.get("/tours").then(r => setTours(r.data));
  }, []);

  const firstUpcoming = instances[0];
  const publicLinks = [
    { l: "Home", h: "/" },
    { l: "All tours", h: "/tours" },
    { l: "All dates", h: "/calendar" },
    { l: "About", h: "/about" },
    { l: "Embed (iframe)", h: "/embed/dates" },
  ];

  const cards = stats ? [
    { l: "Upcoming tours", v: stats.upcoming_instances },
    { l: "Confirmed bookings", v: stats.confirmed_bookings },
    { l: "Total customers", v: stats.total_customers },
    { l: "Total revenue", v: `$${stats.total_revenue.toFixed(2)}` },
  ] : [];

  return (
    <div className="p-10 md:p-12" data-testid="admin-dashboard">
      <p className="label-eyebrow text-ms-stone">Today</p>
      <h1 className="serif text-4xl text-ms-earth mt-2">Dashboard</h1>

      <TodayPanel />

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="bg-white border border-ms-edge rounded-2xl p-6">
            <p className="label-eyebrow text-ms-stone text-xs">{c.l}</p>
            <p className="serif text-3xl text-ms-earth mt-3">{c.v}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-ms-edge rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <h2 className="serif text-xl text-ms-earth">Recent bookings</h2>
            <Link to="/admin/bookings" className="text-xs text-ms-brass hover:text-ms-brass-lo label-eyebrow">View all →</Link>
          </div>
          <div className="mt-4 divide-y divide-ms-edge">
            {bookings.length === 0 ? <p className="text-ms-stone text-sm py-6">No bookings yet.</p> :
              bookings.map(b => (
                <div key={b.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="text-ms-earth">{b.customer_name}</p>
                    <p className="text-ms-stone text-xs">{b.guest_count} guests · {b.status}</p>
                  </div>
                  <p className="text-ms-earth">${b.total_amount}</p>
                </div>
              ))}
          </div>
        </div>
        <div className="bg-white border border-ms-edge rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <h2 className="serif text-xl text-ms-earth">Upcoming instances</h2>
            <Link to="/admin/calendar" className="text-xs text-ms-brass hover:text-ms-brass-lo label-eyebrow">Schedule →</Link>
          </div>
          <div className="mt-4 divide-y divide-ms-edge">
            {instances.length === 0 ? <p className="text-ms-stone text-sm py-6">No upcoming dates scheduled.</p> :
              instances.map(i => {
                const dt = new Date(i.starts_at);
                return (
                  <div key={i.id} className="py-3 flex items-center justify-between text-sm">
                    <p className="text-ms-earth">{dt.toLocaleString(undefined, {month:'short', day:'numeric', hour:'numeric', minute:'2-digit'})}</p>
                    <p className="text-ms-stone">{i.booked_count}/{i.capacity}</p>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* UAT shortcuts */}
      <div className="mt-10 bg-white border border-ms-edge rounded-2xl p-6" data-testid="uat-shortcuts">
        <div className="flex items-center justify-between">
          <h2 className="serif text-xl text-ms-earth">UAT shortcuts</h2>
          <span className="text-xs text-ms-stone">Open in a new tab</span>
        </div>

        <p className="label-eyebrow text-ms-stone text-[10px] mt-5">Public site</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {publicLinks.map(p => (
            <a key={p.h} href={p.h} target="_blank" rel="noreferrer"
               className="px-3 py-1.5 text-xs rounded-full border border-ms-edge text-ms-earth hover:bg-ms-cream hover:border-ms-brass transition-all"
               data-testid={`uat-public-${p.l.toLowerCase().replace(/\s/g,'-')}`}>
              {p.l} ↗
            </a>
          ))}
        </div>

        {tours.length > 0 && (
          <>
            <p className="label-eyebrow text-ms-stone text-[10px] mt-6">Tour detail pages</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {tours.map(t => (
                <a key={t.id} href={`/tour/${t.slug}`} target="_blank" rel="noreferrer"
                   className="px-3 py-1.5 text-xs rounded-full border border-ms-edge text-ms-earth hover:bg-ms-cream hover:border-ms-brass transition-all"
                   data-testid={`uat-tour-${t.slug}`}>
                  {t.title} ↗
                </a>
              ))}
            </div>
          </>
        )}

        {firstUpcoming && (
          <>
            <p className="label-eyebrow text-ms-stone text-[10px] mt-6">Sample checkout (next upcoming instance)</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <a href={`/checkout/${firstUpcoming.id}`} target="_blank" rel="noreferrer"
                 className="px-3 py-1.5 text-xs rounded-full bg-ms-earth text-ms-cream hover:bg-ms-brass transition-all"
                 data-testid="uat-sample-checkout">
                Open checkout ↗
              </a>
            </div>
          </>
        )}

        <p className="label-eyebrow text-ms-stone text-[10px] mt-6">Admin</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {[
            ["Tours", "/admin/tours"],
            ["Calendar", "/admin/calendar"],
            ["Bookings", "/admin/bookings"],
            ["Customers", "/admin/customers"],
            ["Settings", "/admin/settings"],
          ].map(([l, h]) => (
            <Link key={h} to={h}
                  className="px-3 py-1.5 text-xs rounded-full border border-ms-edge text-ms-earth hover:bg-ms-cream hover:border-ms-brass transition-all"
                  data-testid={`uat-admin-${l.toLowerCase()}`}>
              {l}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
